/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from "@/components/ui/Button/Button";
import { InputForm } from "@/components/ui/Input/InputForm";
import { Label } from "@/components/ui/Input/Label";
import { Logo } from "@/components/ui/Logo/Logo";
import { cn } from "@/lib/utils";
import { useResponsive } from "@/utils/useResponsive"
import { Pages } from "@/utils/routes.utils";
import { useNavigate } from "react-router-dom";
import { signupSchema } from "@/store/auth/auth.model";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FormEvent, useEffect } from "react";
import { useSignup } from "@/store/auth/auth.service";
import { useTranslation } from "react-i18next";
import { AuthStore } from "@/store/auth/auth.store";
import { toast } from "sonner";
import PhoneInput, { CountryData } from 'react-phone-input-2'
import fr from 'react-phone-input-2/lang/fr.json'

import 'react-phone-input-2/lib/style.css'
import './styles.css';
import { SettingsStore } from "@/store/settings/settings.store";
import { SelectComponent } from "@/components/Select/Select.component";
import { Lang } from "@/store/wallet/product.model";
import { useBrandConfig } from "@/utils/useBrandConfig";

export const Signup = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { setEmail, loading, setLoading } = AuthStore();

  const { getLanguage, setLanguage } = SettingsStore();
  const lang = getLanguage();


  const { mutate: signup } = useSignup({
    onSuccess: () => {
      setEmail(formValues.email);
      navigate(Pages.PIN);
      setLoading(false);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const {
    formState: { errors, isValid },
    setValue,
    watch,
    trigger,
  } = useForm({
    resolver: zodResolver(signupSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      phoneCode: '',
      language: '',
    },
  })
  const formValues = watch();

  const { name } = useBrandConfig();
  const brandName = name.toLowerCase().replace(/\s+/g, '');

  const languages = [
    {
      label: t('settings.language.english'),
      value: 'EN',
    },
    {
      label: t('settings.language.french'),
      value: 'FR',
    },
    ...(brandName === 'adamlippes' ? [{
      label: t('settings.language.japanese'),
      value: 'JA',
    }] : [])
  ];

  useEffect(() => {
    const fields: (keyof typeof formValues)[] = [
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'phoneCode',
      'language',
    ];

    fields.forEach(field => {
      if (formValues[field]) {
        trigger(field);
      }
    });
  }, [formValues.firstName, formValues.lastName, formValues.email, formValues.phoneNumber, formValues.phoneCode, formValues.language, trigger,]);

  const formatPhoneNumber = (phoneCode: string, phoneNumber: string): string => {
    if (!phoneNumber.startsWith(phoneCode.replace('+', ''))) return phoneNumber;

    const national = phoneNumber.slice(phoneCode.length - 1);
    return `${phoneCode} ${national}`;
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    trigger();
    if (isValid) {
      const phoneCode = `+${formValues.phoneCode}`;
      const phoneNumber = formatPhoneNumber(phoneCode, formValues.phoneNumber);
      setLoading(true);
      signup({
        email: formValues.email,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        phoneCode,
        phoneNumber,
        language: formValues.language,
      });
    }
  }

  const handleBack = () => {
    setLoading(false);
    navigate(Pages.SINGIN);
  }

  const Content = (
    <form
      onSubmit={handleSubmit}
      className="px-7 py-7 h-full flex flex-col justify-start items-center">
      <div className='flex flex-col flex-1 justify-between items-center w-full'>
        <Logo width={180} height={80} />
        <div className={`flex flex-col justify-center items-center w-full ${isMobile ? '' : 'pt-[50px]'}`}>
          <div className="text-headingXLarge mb-2 text-center">
            {t("auth.signup.title")}
          </div>
          <div className="opacity-50 text-center mb-8">
            {t("auth.signup.subtitle")}
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-start items-center w-full">
        <div className={cn(isMobile ? 'flex flex-col w-full' : 'grid grid-cols-2 gap-3 w-[732px]')}>
          <InputForm
            value={formValues.firstName}
            error={errors.firstName?.message}
            handleChange={(value) => setValue('firstName', value)}
            required
            label={t("inputs.firstName.label")}
            placeholder={t("inputs.firstName.placeholder")}
          />
          <InputForm
            value={formValues.lastName}
            error={errors.lastName?.message}
            handleChange={(value) => setValue('lastName', value)}
            required
            label={t("inputs.lastName.label")}
            placeholder={t("inputs.lastName.placeholder")}
            className={isMobile ? 'mt-3' : ''}
          />
          <InputForm
            value={formValues.email}
            error={errors.email?.message}
            handleChange={(value) => setValue('email', value)}
            required
            label={t("inputs.email.label")}
            placeholder={t("inputs.email.placeholder")}
            className={isMobile ? 'mt-3' : ''}
            type="email"
          />
          <div className={cn(isMobile ? 'mt-3' : '')}>
            <div className="flex flex-row justify-start w-full">
              <Label>{t("inputs.phoneNumber.label")}</Label>
            </div>
            <PhoneInput
              country={'fr'}
              value={formValues.phoneNumber}
              onChange={(value, code) => {
                setValue('phoneNumber', value);
                setValue('phoneCode', (code as CountryData).dialCode);
              }}
              inputClass="!w-full !h-[52px] !bg-[#F7F7F7] !rounded-sm !px-[20px] !pl-[58px] !text-bodyLarge !text-black !placeholder-[#7C7C7C] !placeholder:text-bodySmall !border !border-[rgba(0,0,0,0.05)] focus:!ring-1 focus:!ring-ring focus:!outline-none"
              containerClass="w-full"
              buttonClass="!bg-transparent !border-none !pl-[16px]"
              countryCodeEditable={false}
              preferredCountries={['fr']}
              localization={lang === 'FR' ? fr : {}}
              enableSearch={true}
              searchPlaceholder={t("inputs.phoneNumber.search")}
              disableSearchIcon={true}
            />
          </div>
          <SelectComponent
            className={isMobile ? 'mt-6' : 'mt-3'}
            options={languages}
            label={t('inputs.language.label')}
            handleChange={(v) => {
              setValue('language', v);
              setLanguage(v as Lang);
            }}
            value={lang}
            placeholder={t('inputs.nationality.placeholder')}
            labelClassName="text-bodyLarge leading-none pb-3 group-disabled:cursor-not-allowed group-disabled:opacity-70"
          />
        </div>
        <div className={`flex gap-4 ${isMobile ? 'flex-col-reverse pt-8' : 'flex-row py-12'}`}>
          <Button
            label={t("buttons.back")}
            variant="SoftRounded"
            size={isMobile ? "large" : "small"}
            onClick={handleBack}
          />
          <Button
            label={t("buttons.createAccount")}
            primary
            variant="SoftRounded"
            size={isMobile ? "large" : "small"}
            onClick={() => { }}
            type='submit'
            loading={loading}
          />
        </div>
      </div>
    </form>
  );

  return isMobile ? Content : (
    <div className="flex justify-center items-start pt-[100px]">
      {Content}
    </div>
  );
}